import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ProfileType } from "@/services/profile.service";
import { useUpdateProfileMutation } from "@/hooks/api/profile.queries";
import { Save, X, Building2 } from "lucide-react";

const formSchema = z.object({
  industry: z.string().optional(),
  company_size: z.string().optional(),
  headquarters: z.string().optional(),
  founded_year: z.union([z.string(), z.number()]).optional(),
  description: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ProfileFormProps {
  initialData: ProfileType;
  onCancel: () => void;
}

const ProfileForm = ({ initialData, onCancel }: ProfileFormProps) => {
  const updateProfileMutation = useUpdateProfileMutation();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      industry: initialData.industry ?? "",
      company_size: initialData.company_size ?? "",
      headquarters: initialData.headquarters ?? "",
      founded_year: initialData.founded_year ?? "",
      description: initialData.description ?? "",
    },
  });

  const onSubmit = (values: FormValues) => {
    updateProfileMutation.mutate(
      {
        industry: values.industry || undefined,
        company_size: values.company_size || undefined,
        headquarters: values.headquarters || undefined,
        // Only safely convert to number if it's not empty
        founded_year: values.founded_year ? Number(values.founded_year) : undefined,
        description: values.description || undefined,
      },
      {
        onSuccess: () => {
          onCancel();
        },
      }
    );
  };

  return (
    <div className="flex flex-col w-full h-full pb-10 space-y-6">

      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-border/40 pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            Edit Organization Profile
          </h1>
          <p className="text-muted-foreground mt-1">Update your company details and public information.</p>
        </div>

        <div className="flex gap-3 w-full md:w-auto">
          <Button variant="outline" onClick={onCancel} disabled={updateProfileMutation.isPending} className="flex-1 md:flex-none">
            <X className="mr-2 h-4 w-4" /> Cancel
          </Button>
          <Button onClick={form.handleSubmit(onSubmit)} disabled={updateProfileMutation.isPending} className="flex-1 md:flex-none">
            <Save className="mr-2 h-4 w-4" />
            {updateProfileMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Main Form Content */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 w-full max-w-4xl">

          <Card className="border-border/40 shadow-sm">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>Core details about your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry / Sector</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. Information Technology" className="bg-background" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="company_size"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Size</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. 50-200" className="bg-background" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="headquarters"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Headquarters Location</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g. San Francisco, CA" className="bg-background" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="founded_year"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Founded Year</FormLabel>
                      <FormControl>
                        <Input type="number" placeholder="e.g. 2010" className="bg-background" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

            </CardContent>
          </Card>

          <Card className="border-border/40 shadow-sm">
            <CardHeader>
              <CardTitle>About Organization</CardTitle>
              <CardDescription>Provide a comprehensive overview of your company</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Share your mission, vision, and what makes your organization unique..."
                        className="min-h-[200px] resize-y bg-background"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

        </form>
      </Form>
    </div>
  );
};

export default ProfileForm;
